import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { CONTENT_STATUS } from '@mathflat/domain/@entities/(Content)/module'
import clsx from 'clsx'
import { observer } from 'mobx-react'
import { type ReactNode, useEffect, useRef } from 'react'

import { useDraggable } from '~/@common/hooks/useDraggable'
import { CustomEventService } from '~/@common/services/event.service'
import { colors } from '~/@common/styles'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import type { StyleProps } from '~/@common/types'
import { VirtualKeyboardFloater } from '~/@common/ui/(Keypad)'
import { virtualKeyboardService } from '~/@common/ui/(Keypad)/keypad.service'
import { MainModule } from '~/@pages/@widgets/Main'

import type { ProblemScoringColl } from '../(ProblemScoring)/@trait/ProblemScoring.trait'
import type { ProblemScoringViewOption } from '../(ProblemScoring)/@trait/ProblemScoringViewOption.trait'
import ProblemScoringCard from '../(ProblemScoring)/ProblemScoringCard/ProblemScoringCard'
import type { ProblemScoringFooterProps } from '../(ProblemScoring)/ProblemScoringCard/ProblemScoringFooter'
import { CardGridContainerModule } from '../CardGridContainer'
import CardGridContainer from '../CardGridContainer/CardGridContainer'
import type { Props as StudentEduMaterialScoringDashboardProps } from '../StudentEduMaterialScoringDashboard'
import { StudentEduMaterialScoringDashboard } from '../StudentEduMaterialScoringDashboard'
import { showDashboard as computeShowDashboard } from './QuickScoring.utils'

type Props<T extends 'WORKSHEET' | 'WORKBOOK'> = {
  type: T
  contentId?: string
  viewOption?: ProblemScoringViewOption<'학습모듈' | 'NOT_학습모듈'>
  problemScoringColl?: ProblemScoringColl<T>
  score: StudentEduMaterialScoringDashboardProps['score']
  scoring: StudentEduMaterialScoringDashboardProps['scoring']
  onLocalUserInputChange?: ProblemScoringFooterProps['onLocalUserInputChange']
  onSubmitAnswerImmediately?: ProblemScoringFooterProps['onSubmitAnswerImmediately']
  dashboardSlot?: ReactNode
  supplementaryLearningSlot?: ReactNode
} & StyleProps

export const parts = { wrapper: 'ms__QuickScoring' }

// 학습지류 = 빠른채점 리스트
// 교재 = detail
export const QuickScoringTemplate = observer(
  <T extends 'WORKSHEET' | 'WORKBOOK'>({
    contentId,
    viewOption,
    problemScoringColl,
    className,
    type,
    score,
    scoring,
    onLocalUserInputChange,
    onSubmitAnswerImmediately,
    dashboardSlot,
    supplementaryLearningSlot,
    ...props
  }: Props<T>) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const droppableEl = document.querySelector(`.${MainModule.base}`) as HTMLElement

    const { isDragging, dragDelta, draggableRef } = useDraggable<HTMLElement>({
      containerEl: droppableEl,
    })

    useEffect(() => {
      const { disposer } = CustomEventService.alltooltipOff.listen(scrollRef.current)
      return () => {
        disposer()
      }
    }, [scrollRef, viewOption])

    if (!viewOption) return null

    const showDashboard = computeShowDashboard(viewOption)

    return (
      <S.QuickScoringTemplate
        {...props}
        className={clsx(parts.wrapper, viewOption.문제같이보기 && '문제같이보기', className)}
        ref={scrollRef}
      >
        {showDashboard && (
          <div
            className={clsx(
              'dashboard-wrapper',
              (!dashboardSlot || !supplementaryLearningSlot) && 'dashboard-wrapper-missing-slot',
            )}
          >
            <StudentEduMaterialScoringDashboard
              eduMaterialType={type}
              scoring={scoring}
              score={score}
              showScore
              status={viewOption.content.status}
            />
            {dashboardSlot && <div className="dashboard-slot">{dashboardSlot}</div>}
          </div>
        )}
        {showDashboard && supplementaryLearningSlot && (
          <div className="supplementary-learning-wrapper">{supplementaryLearningSlot}</div>
        )}
        {viewOption.content.status !== CONTENT_STATUS['채점전'] &&
        viewOption.오답_모르는문제만보기 &&
        problemScoringColl?.toWrongOrUnknwonArr.length === 0 ? (
          <S.Empty className="hasNotWrongOrUnknown">오답/모르는 문제가 없습니다.</S.Empty>
        ) : (
          <CardGridContainer
            className="problem-card-grid"
            ratio={viewOption.문제같이보기 ? 'fr1' : 'fr3'}
          >
            {problemScoringColl?.toArr.map((problemScoring) => {
              return (
                <ProblemScoringCard
                  contentId={contentId}
                  key={problemScoring.id}
                  problemScoring={problemScoring}
                  viewOption={viewOption}
                  isSubmittedAnswerAvailable={type !== 'WORKBOOK'}
                  onLocalUserInputChange={onLocalUserInputChange}
                  onSubmitAnswerImmediately={onSubmitAnswerImmediately}
                />
              )
            })}
          </CardGridContainer>
        )}
        {virtualKeyboardService.show && virtualKeyboardService.showType === 'floater' && (
          <VirtualKeyboardFloater
            ref={draggableRef}
            style={{
              position: 'absolute',
              transform: `translate(${dragDelta.x}px, ${dragDelta.y}px)`,
              outline: isDragging ? '1px solid var(--Colors-Blue-600)' : 'none',
            }}
            closeKeyboard={() => virtualKeyboardService.close()}
            onKeyButtonClick={(e, latexNodeType) => {
              virtualKeyboardService.onKeyButtonClick(e, latexNodeType)
            }}
            keypadType={virtualKeyboardService.type}
          />
        )}
      </S.QuickScoringTemplate>
    )
  },
)

export const mobileStyle = css`
  flex-direction: column;
  gap: 0;
  overflow-x: hidden;
  overflow-y: auto;
  flex: 0 1 100%;

  .${CardGridContainerModule.parts.gridContainer} {
    padding: 20px 0;
    height: auto;
  }

  .dashboard-wrapper {
    height: auto;
    padding: 10px 16px 0;
    margin: 0;
    gap: 10px;
    &.dashboard-wrapper-missing-slot {
      height: auto;
    }
  }

  .supplementary-learning-wrapper {
    width: 100%;
    padding: 10px 16px 0;
    margin: 0;
    height: auto;
  }

  .dashboard-wrapper + .supplementary-learning-wrapper {
    padding-top: 10px;
  }

  .supplementary-learning-wrapper + .${CardGridContainerModule.parts.gridContainer} {
    padding: 10px 0;
  }

  .dashboard-wrapper + .${CardGridContainerModule.parts.gridContainer} {
    padding: 10px 0;
  }
`

const S = {
  QuickScoringTemplate: styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    gap: 24px;
    overflow-x: auto;

    &.문제같이보기 {
      height: 100%;
    }

    .dashboard-wrapper {
      display: flex;
      flex-direction: column;
      gap: 24px;
      margin: auto 0;
      flex-shrink: 0;

      &.dashboard-wrapper-missing-slot {
        height: 460px;
      }

      .dashboard-description {
        display: flex;
        gap: 2px;
        align-items: center;

        margin-top: 12px;
        color: ${colors.gray.$500};

        ${mediaQuery.underTablet} {
          justify-content: center;
        }
      }
    }

    .supplementary-learning-wrapper {
      display: flex;
      flex-direction: column;
      margin: auto 0;
      flex-shrink: 0;
      height: 100%;
      max-height: 525px;
    }

    ${mediaQuery.underTablet} {
      ${mobileStyle};
    }
  `,

  Empty: styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  `,
}
